import get from 'lodash/get';
import { action, autorun, computed, makeObservable, observable } from 'mobx';

import { SOCKET_USER_WALLET_BALANCE } from '../../config/constants';
import {
  type WalletResponse,
  type Wallet,
  type LastBet,
  type RubyLastBet,
  type WalletNotification,
} from '../../dto/wallet.types';
import { analytics } from '../../services/analytics';
import { getRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

const EMPTY_WALLET: Wallet = {
  gcCoin: 0,
  scCoin: {
    bsc: 0,
    psc: 0,
    wsc: 0,
  },
  totalScCoin: 0,
  cash: 0,
  cashOffer: null,
  walletId: -1,
  currencyCode: '',
};

export type CustomBalance = {
  gcCoin?: number;
  scCoin?: number;
  cash?: number;
};

export class WalletStore extends Loadable {
  private readonly rootStore: RootStore;
  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    const subscription = this.subscribeToSocket.bind(this);
    autorun(() => {
      if (this.rootStore.authStore.authorized) {
        socket.on(SOCKET_USER_WALLET_BALANCE, subscription);
      } else {
        this.setWallet({ ...EMPTY_WALLET });
        socket.off(SOCKET_USER_WALLET_BALANCE, subscription);
      }
    });
  }

  @observable
  public lastBet: LastBet = {
    GC: null,
    SC: null,
  };

  @observable
  public rubyLastBet: RubyLastBet = {
    GC: {
      lastBet: 0,
      balance: 0,
    },
    SC: {
      lastBet: 0,
      balance: 0,
    },
  };

  @observable
  public wallet: Wallet = {
    ...EMPTY_WALLET,
  };

  @observable
  public isFreeSpinsActive = false;

  @observable
  public customBalanceOverride: CustomBalance | null = null;

  @computed
  public get userGcBalance() {
    return this.wallet.gcCoin;
  }

  @action
  public setWallet(partialWallet: Partial<Wallet>): void {
    this.wallet = {
      ...this.wallet,
      ...partialWallet,
    };
    analytics.setBalanceProps(this.wallet);
  }

  @action
  public setCustomBalance(balance: CustomBalance | null): void {
    this.customBalanceOverride = balance;
  }

  @action
  public clearCustomBalance(): void {
    this.setCustomBalance(null);
  }

  @computed
  public get balance() {
    const balance = {
      gcCoin: this.wallet.gcCoin || 0,
      scCoin: this.wallet.totalScCoin || 0,
      cash: this.wallet.cash || 0,
      // Server-computed dynamic cash offer riding on every wallet payload (MZ-454).
      cashOffer: this.wallet.cashOffer ?? null,
    };
    return balance;
  }

  @computed
  public get currentCoinValue() {
    const key = this.rootStore.coinTypeStore.coinType === 'GC' ? 'gcCoin' : 'scCoin';
    return this.balance[key];
  }

  @computed
  public get currentCustomValue() {
    const key = this.rootStore.coinTypeStore.coinType === 'GC' ? 'gcCoin' : 'scCoin';
    if (!this.customBalanceOverride) {
      return null;
    }
    return this.customBalanceOverride[key] || 0;
  }

  public updateWallet(wallet: Partial<Wallet>) {
    this.setWallet(wallet);
  }

  public resetWallet() {
    this.setWallet({ ...EMPTY_WALLET });
    this.clearCustomBalance();
  }

  public async fetchWallet() {
    try {
      this.setLoading(true);
      const response = await getRequest<WalletResponse>('/v2/user/get-wallet');

      const wallet = get(response, 'data.data.userWallet', {}) as Wallet;

      this.setWallet(wallet);
    } finally {
      this.setLoading(false);
    }
  }

  private subscribeToSocket(event: WalletNotification) {
    const updateThroughNotificationFlag = resolveFlag('update_balance_through_notification', false);
    logToNewRelic(
      'WalletStore: wallet update socket message',
      {
        event,
        updateThroughNotificationFlag,
        message: 'Wallet socket message received',
      },
      'info',
    );

    const payload = get(event, 'data');
    if (!payload?.state || !updateThroughNotificationFlag) {
      void this.fetchWallet();
      return;
    }

    try {
      const state = get(payload, 'state');
      this.setWallet({
        gcCoin: state.gc,
        totalScCoin: payload.scCoin,
        cash: payload.cash || 0,
        // Always overwrite from the server payload — a missing/null offer means
        // the new balance is no longer eligible, so a stale offer must not survive.
        cashOffer: payload.cashOffer ?? null,
        scCoin: {
          wsc: state.wsc,
          bsc: state.bsc,
          psc: state.psc,
        },
      });
    } catch (e) {
      logToNewRelic('WalletStore.subscribeToSocket', (e as Error).message, 'error');
      void this.fetchWallet();
    }
  }
}
